import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {JobStatusesEnum, TagJobModel} from '../../../../models/api/jobModel';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerProgress} from '../../../common/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';
import {BannerSecondaryText} from '../../../common/banners/bannerSecondaryText';
import {BannerText} from '../../../common/banners/bannerText';

/*
 * Props.
 */

interface CustomViewBackfillBannerProps {
  job: TagJobModel;
  onDismissClick: Handler;
}

/*
 * Component.
 */

export const CustomViewBackfillBanner: FC<CustomViewBackfillBannerProps> = (props) => {
  const theme = useUserTheme();
  const {job, onDismissClick} = props;
  const {status, stepCount, completedStepCount} = job;

  if (status === JobStatusesEnum.FAILED) {
    return (
      <Banner
        bannerType={BannerTypesEnum.INLINE}
        priority={BannerPrioritiesEnum.ERROR}
        color={theme.banner.warningBackground}
      >
        <BannerCloseAction onClick={onDismissClick} />
        <BannerIcon iconName="warningFilledSmall" />
        <BannerText>
          <Trans id="banner-customviewbackfill-failed">View setup failed</Trans>
        </BannerText>
      </Banner>
    );
  }

  if (status === JobStatusesEnum.DONE) {
    return (
      <Banner
        bannerType={BannerTypesEnum.INLINE}
        priority={BannerPrioritiesEnum.TRANSIENT}
        color={theme.banner.warningBackground}
      >
        <BannerCloseAction onClick={onDismissClick} />
        <BannerIcon iconName="checkmarkCircleSmall" />
        <BannerText>
          <Trans id="banner-customviewbackfill-complete">View setup complete</Trans>
        </BannerText>
      </Banner>
    );
  }

  const progress = stepCount === 0 ? 0 : completedStepCount / stepCount;
  const percentProgress = Math.floor(100 * progress);

  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.TRANSIENT}>
      <BannerProgress progress={progress} />
      <BannerIcon iconName="checkmarkCircleSmall" />
      <BannerText>
        <Trans id="banner-customviewbackfill-backfill">Setting up your new view...</Trans>
      </BannerText>
      <BannerSecondaryText>
        <Trans id="banner-customviewbackfill-progress">{percentProgress}%</Trans>
      </BannerSecondaryText>
    </Banner>
  );
};
